import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { ArrowLeft, Camera, Printer } from 'lucide-react';
import { db } from '../../firebase/config';
import { useHiddenStudentRoute } from '../../utils/studentRoutes';
import { getStudentPhotoUrl } from '../../utils/mediaUrls';
import { getConfiguredSubjects, getPerformanceRemark, normalizeSubjectKey, SEMESTER_MONTHS } from '../../utils/semesterConfig';

const legacySubjectKeys = {
  ami: 'amI',
  am1: 'amI',
  appliedmathematicsi: 'amI',
  amii: 'amII',
  am2: 'amII',
  ap: 'ap',
  appliedphysics: 'ap',
  ac: 'ac',
  appliedchemistry: 'ac',
  em: 'em',
  engineeringmechanics: 'em',
  bee: 'bee',
  basicelectricalandelectronicsengineering: 'bee',
  pce: 'pce',
  professionalcommunicationandethics: 'pce'
};

const valueOrDash = (value) => (
  value === undefined || value === null || value === '' ? '-' : value
);

const numberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const percentage = (attended, total) => (
  total > 0 ? `${((attended / total) * 100).toFixed(2)}%` : '-'
);

const studentName = (student) => (
  `${student?.firstName || ''} ${student?.surname || ''}`.trim() ||
  student?.name ||
  student?.rollNumber ||
  '-'
);

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const marksDocId = (studentRollNumber, semester, subjectId) => (
  `${studentRollNumber}_semester${semester}_${subjectId}`.replace(/[^a-z0-9_-]/gi, '_')
);

const hasMark = (value) => value !== '' && value !== undefined && value !== null;

const getCourseMark = (record, examKey) => (
  record?.[examKey]?.marks ?? record?.[`${examKey}Marks`] ?? ''
);

const getCourseRemark = (record, examKey) => (
  record?.[examKey]?.remark ?? record?.[`${examKey}Remark`] ?? ''
);

const formatMark = (value, max) => (
  hasMark(value) ? `${valueOrDash(value)}/${valueOrDash(max)}` : '-'
);

const formatTotal = (hasAnyMark, total, max) => (
  hasAnyMark ? `${total}/${max}` : '-'
);

const clampMarkForDisplay = (value, maxMarks) => {
  if (!hasMark(value)) return '';

  const numeric = Number(value);
  const maximum = Number(maxMarks);
  if (!Number.isFinite(numeric)) return value;
  if (Number.isFinite(maximum) && maximum > 0 && numeric > maximum) return maximum;
  return value;
};

const subjectAppliesToStudent = (subject, currentStudent) => (
  !subject.teachingDivision || subject.teachingDivision === currentStudent?.division
);

const remarkOrAuto = (remark, marks, maxMarks) => {
  if (remark && remark !== '-') return remark;
  return getPerformanceRemark(marks, maxMarks);
};

const getSubjectMark = (iaRecord, subject) => {
  const marks = iaRecord?.subjects || {};
  const directKeys = [
    subject.id,
    subject.code,
    subject.name,
    legacySubjectKeys[normalizeSubjectKey(subject.id)],
    legacySubjectKeys[normalizeSubjectKey(subject.code)],
    legacySubjectKeys[normalizeSubjectKey(subject.name)]
  ].filter(Boolean);

  for (const key of directKeys) {
    if (marks[key] !== undefined) return marks[key];
  }

  const normalizedMarks = Object.entries(marks).reduce((acc, [key, value]) => {
    acc[normalizeSubjectKey(key)] = value;
    return acc;
  }, {});

  return normalizedMarks[normalizeSubjectKey(subject.id)] ??
    normalizedMarks[normalizeSubjectKey(subject.code)] ??
    normalizedMarks[normalizeSubjectKey(subject.name)] ??
    '';
};

const getAttendanceEntry = (attendance, month) => {
  if (!attendance) return null;
  const entries = Array.isArray(attendance.entries) ? attendance.entries : [];
  return entries.find((entry) => entry.month === month) ||
    (attendance.month === month ? attendance : null);
};

const getAttendanceNumbers = (entry) => {
  if (!entry) {
    return {
      theoryAttended: 0,
      theoryConducted: 0,
      practicalAttended: 0,
      practicalConducted: 0
    };
  }

  return {
    theoryAttended: numberOrZero(entry.theoryTotal?.attended ?? entry.theoryAttended),
    theoryConducted: numberOrZero(entry.theoryTotal?.conducted ?? entry.theoryConducted),
    practicalAttended:
      numberOrZero(entry.practicalTotal?.attended ?? entry.practicalAttended) +
      numberOrZero(entry.tutorialTotal?.attended ?? entry.tutorialAttended),
    practicalConducted:
      numberOrZero(entry.practicalTotal?.conducted ?? entry.practicalConducted) +
      numberOrZero(entry.tutorialTotal?.conducted ?? entry.tutorialConducted)
  };
};

const getCourseAttendanceNumbers = (record) => {
  const theoryAttended = numberOrZero(record?.theoryTotal?.attended ?? record?.theoryAttended);
  const theoryConducted = numberOrZero(record?.theoryTotal?.conducted ?? record?.theoryConducted);
  const practicalAttended =
    numberOrZero(record?.practicalTotal?.attended ?? record?.practicalAttended) +
    numberOrZero(record?.tutorialTotal?.attended ?? record?.tutorialAttended);
  const practicalConducted =
    numberOrZero(record?.practicalTotal?.conducted ?? record?.practicalConducted) +
    numberOrZero(record?.tutorialTotal?.conducted ?? record?.tutorialConducted);

  return {
    theoryAttended,
    theoryConducted,
    practicalAttended,
    practicalConducted,
    attended: theoryAttended + practicalAttended,
    conducted: theoryConducted + practicalConducted
  };
};

const attendanceCell = (attended, conducted) => (
  conducted > 0 ? `${attended}/${conducted}` : '-'
);

const attendancePercentValue = (attended, conducted) => (
  conducted > 0 ? (attended / conducted) * 100 : null
);

const attendanceRemark = (attended, conducted) => {
  const percentValue = attendancePercentValue(attended, conducted);
  if (percentValue === null) return '-';
  return percentValue < 75 ? 'Defaulter' : 'Regular';
};

const shortMonth = (month) => String(month || '').slice(0, 3);

const EndSemesterReport = () => {
  const { rollNumber, navigate } = useHiddenStudentRoute('/student/end-semester-report');
  const reportRef = useRef(null);
  const [semester, setSemester] = useState('1');
  const [student, setStudent] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [iaTest1, setIaTest1] = useState(null);
  const [iaTest2, setIaTest2] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [courseMarks, setCourseMarks] = useState({});
  const [courseAttendance, setCourseAttendance] = useState({});
  const [parentMeet, setParentMeet] = useState(null);
  const [mentorName, setMentorName] = useState('N/A');
  const [coordinatorName, setCoordinatorName] = useState('N/A');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!rollNumber) {
      setLoading(false);
      return;
    }
    loadReport();
  }, [rollNumber, semester]);

  const loadReport = async () => {
    setLoading(true);

    try {
      const [
        studentDoc,
        attendanceDoc,
        iaTest1Doc,
        iaTest2Doc,
        subjectsDoc,
        courseMarksSnap,
        courseAttendanceSnap,
        parentMeetDoc
      ] = await Promise.all([
        getDoc(doc(db, 'students', rollNumber)),
        getDoc(doc(db, 'attendance', rollNumber)),
        getDoc(doc(db, 'iaTest1', rollNumber)),
        getDoc(doc(db, 'iaTest2', rollNumber)),
        getDoc(doc(db, 'semesterSubjects', `semester${semester}`)),
        getDocs(collection(db, 'courseMarks')),
        getDocs(collection(db, 'courseAttendance')),
        getDoc(doc(db, 'parentMeetRecords', `${rollNumber}_semester${semester}`))
      ]);

      const studentData = studentDoc.exists() ? studentDoc.data() : null;
      setStudent(studentData);
      setAttendance(attendanceDoc.exists() ? attendanceDoc.data() : null);
      setIaTest1(iaTest1Doc.exists() ? iaTest1Doc.data() : null);
      setIaTest2(iaTest2Doc.exists() ? iaTest2Doc.data() : null);
      setSubjects(getConfiguredSubjects(subjectsDoc.exists() ? subjectsDoc.data() : null, Number(semester)));
      const nextCourseMarks = {};
      courseMarksSnap.docs.forEach((markDoc) => {
        const mark = markDoc.data();
        const sameStudent = mark.rollNumber === rollNumber || markDoc.id.startsWith(`${rollNumber}_`);
        const sameSemester = Number(mark.semester) === Number(semester);
        if (sameStudent && sameSemester) {
          nextCourseMarks[markDoc.id] = mark;
        }
      });
      setCourseMarks(nextCourseMarks);
      const nextCourseAttendance = {};
      courseAttendanceSnap.docs.forEach((attendanceDoc) => {
        const record = attendanceDoc.data();
        const sameStudent = record.rollNumber === rollNumber || attendanceDoc.id.startsWith(`${rollNumber}_`);
        const sameSemester = Number(record.semester) === Number(semester);
        if (sameStudent && sameSemester) {
          nextCourseAttendance[attendanceDoc.id] = record;
        }
      });
      setCourseAttendance(nextCourseAttendance);
      setParentMeet(parentMeetDoc.exists() ? parentMeetDoc.data() : null);

      if (studentData?.batch) {
        const batchDoc = await getDoc(doc(db, 'batchAssignments', studentData.batch));
        setMentorName(batchDoc.exists() ? batchDoc.data().mentorName || 'N/A' : 'N/A');
      }

      if (studentData?.division) {
        const divisionDoc = await getDoc(doc(db, 'divisionAssignments', studentData.division));
        setCoordinatorName(divisionDoc.exists()
          ? divisionDoc.data().ccName || divisionDoc.data().coordinatorName || 'N/A'
          : 'N/A');
      }
    } catch (error) {
      console.error('Error loading end semester report:', error);
    } finally {
      setLoading(false);
    }
  };

  const months = SEMESTER_MONTHS[Number(semester)] || SEMESTER_MONTHS[1];

  const aggregateAttendanceRows = useMemo(() => months.map((month) => {
    const entry = getAttendanceEntry(attendance, month);
    const numbers = getAttendanceNumbers(entry);
    const totalAttended = numbers.theoryAttended + numbers.practicalAttended;
    const totalConducted = numbers.theoryConducted + numbers.practicalConducted;

    return {
      month,
      ...numbers,
      totalAttended,
      totalConducted
    };
  }), [attendance, months]);

  const attendanceMatrixRows = useMemo(() => {
    const courseAttendanceRecords = Object.values(courseAttendance);
    return subjects
      .filter((subject) => subjectAppliesToStudent(subject, student))
      .map((subject) => {
        const monthCells = months.map((month) => {
          const matchingRecords = courseAttendanceRecords.filter((record) => (
            record.month === month &&
            (record.subjectId === subject.id ||
              record.subjectCode === subject.code ||
              normalizeSubjectKey(record.subjectName) === normalizeSubjectKey(subject.name))
          ));
          const numbers = matchingRecords.reduce((acc, record) => {
            const current = getCourseAttendanceNumbers(record);
            return {
              theoryAttended: acc.theoryAttended + current.theoryAttended,
              theoryConducted: acc.theoryConducted + current.theoryConducted,
              practicalAttended: acc.practicalAttended + current.practicalAttended,
              practicalConducted: acc.practicalConducted + current.practicalConducted,
              attended: acc.attended + current.attended,
              conducted: acc.conducted + current.conducted
            };
          }, {
            theoryAttended: 0,
            theoryConducted: 0,
            practicalAttended: 0,
            practicalConducted: 0,
            attended: 0,
            conducted: 0
          });

          return {
            month,
            ...numbers
          };
        });
        const totalAttended = monthCells.reduce((sum, item) => sum + item.attended, 0);
        const totalConducted = monthCells.reduce((sum, item) => sum + item.conducted, 0);

        return {
          ...subject,
          monthCells,
          totalAttended,
          totalConducted,
          percentValue: attendancePercentValue(totalAttended, totalConducted),
          remark: attendanceRemark(totalAttended, totalConducted)
        };
      });
  }, [courseAttendance, months, student, subjects]);

  const hasSubjectAttendance = attendanceMatrixRows.some((row) => row.totalConducted > 0);

  const semesterAttendance = useMemo(() => {
    if (hasSubjectAttendance) {
      return {
        totalAttended: attendanceMatrixRows.reduce((sum, row) => sum + row.totalAttended, 0),
        totalConducted: attendanceMatrixRows.reduce((sum, row) => sum + row.totalConducted, 0),
        monthCells: months.map((month) => {
          const monthRows = attendanceMatrixRows.map((row) => row.monthCells.find((cell) => cell.month === month));
          const theoryAttended = monthRows.reduce((sum, cell) => sum + numberOrZero(cell?.theoryAttended), 0);
          const theoryConducted = monthRows.reduce((sum, cell) => sum + numberOrZero(cell?.theoryConducted), 0);
          const practicalAttended = monthRows.reduce((sum, cell) => sum + numberOrZero(cell?.practicalAttended), 0);
          const practicalConducted = monthRows.reduce((sum, cell) => sum + numberOrZero(cell?.practicalConducted), 0);
          return {
            month,
            theoryAttended,
            theoryConducted,
            practicalAttended,
            practicalConducted,
            attended: theoryAttended + practicalAttended,
            conducted: theoryConducted + practicalConducted
          };
        })
      };
    }

    return {
      totalAttended: aggregateAttendanceRows.reduce((sum, row) => sum + row.totalAttended, 0),
      totalConducted: aggregateAttendanceRows.reduce((sum, row) => sum + row.totalConducted, 0),
      monthCells: aggregateAttendanceRows.map((row) => ({
        month: row.month,
        theoryAttended: row.theoryAttended,
        theoryConducted: row.theoryConducted,
        practicalAttended: row.practicalAttended,
        practicalConducted: row.practicalConducted,
        attended: row.totalAttended,
        conducted: row.totalConducted
      }))
    };
  }, [aggregateAttendanceRows, attendanceMatrixRows, hasSubjectAttendance, months]);

  const academicRows = useMemo(() => subjects
    .filter((subject) => subjectAppliesToStudent(subject, student))
    .map((subject) => {
    const markRecord = courseMarks[marksDocId(rollNumber, semester, subject.id)];
    const rawIa1 = hasMark(getCourseMark(markRecord, 'iat1'))
      ? getCourseMark(markRecord, 'iat1')
      : getSubjectMark(iaTest1, subject);
    const rawIa2 = hasMark(getCourseMark(markRecord, 'iat2'))
      ? getCourseMark(markRecord, 'iat2')
      : getSubjectMark(iaTest2, subject);
    const rawEse = getCourseMark(markRecord, 'ese');
    const ia1 = clampMarkForDisplay(rawIa1, subject.iat1Max || subject.iatMax);
    const ia2 = clampMarkForDisplay(rawIa2, subject.iat2Max || subject.iatMax);
    const ese = clampMarkForDisplay(rawEse, subject.eseMax);

    return {
      ...subject,
      ia1,
      ia1Remark: remarkOrAuto(getCourseRemark(markRecord, 'iat1'), ia1, subject.iat1Max || subject.iatMax),
      ia2,
      ia2Remark: remarkOrAuto(getCourseRemark(markRecord, 'iat2'), ia2, subject.iat2Max || subject.iatMax),
      ese,
      eseRemark: remarkOrAuto(getCourseRemark(markRecord, 'ese'), ese, subject.eseMax)
    };
  }), [courseMarks, iaTest1, iaTest2, rollNumber, semester, student, subjects]);

  const totalIa1Max = academicRows.reduce((sum, subject) => sum + numberOrZero(subject.iat1Max || subject.iatMax), 0);
  const totalIa2Max = academicRows.reduce((sum, subject) => sum + numberOrZero(subject.iat2Max || subject.iatMax), 0);
  const totalEseMax = academicRows.reduce((sum, subject) => sum + numberOrZero(subject.eseMax), 0);
  const totalIa1 = academicRows.reduce((sum, subject) => sum + numberOrZero(subject.ia1), 0);
  const totalIa2 = academicRows.reduce((sum, subject) => sum + numberOrZero(subject.ia2), 0);
  const totalEse = academicRows.reduce((sum, subject) => sum + numberOrZero(subject.ese), 0);
  const hasIa1 = academicRows.some((subject) => hasMark(subject.ia1));
  const hasIa2 = academicRows.some((subject) => hasMark(subject.ia2));
  const hasEse = academicRows.some((subject) => hasMark(subject.ese));

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-700" />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <p className="text-xl text-gray-600">Student data not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-4 print:bg-white print:py-0">
      <div className="mx-auto max-w-[210mm] px-4 print:px-0">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 print:hidden">
          <button
            type="button"
            onClick={() => navigate('/student/dashboard')}
            className="inline-flex items-center rounded-lg bg-gray-700 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </button>

          <label className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow">
            Semester
            <select
              value={semester}
              onChange={(event) => setSemester(event.target.value)}
              className="rounded border border-gray-300 px-2 py-1"
            >
              <option value="1">Semester 1</option>
              <option value="2">Semester 2</option>
            </select>
          </label>

          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
          >
            <Printer className="mr-2 h-4 w-4" />
            Print / Save PDF
          </button>
        </div>

        <div
          ref={reportRef}
          className="bg-white p-[10mm] shadow-lg print:shadow-none"
          style={{ width: '210mm', minHeight: '297mm', margin: '0 auto' }}
        >
          <img
            src="/ybit-letterhead.png"
            alt="YBIT Letterhead"
            className="mb-2 h-auto w-full object-contain"
            style={{ maxHeight: '72px' }}
          />

          <div className="border-b-2 border-black pb-2 text-center">
            <h1 className="text-lg font-bold uppercase tracking-wide">
              End of Semester Report for Semester {semester}
            </h1>
            <p className="text-xs text-gray-600">Digitally generated mentoring record</p>
          </div>

          <div className="mt-3 grid grid-cols-[1fr_30mm] gap-3 text-[11px]">
            <div className="grid grid-cols-2 gap-x-5 gap-y-1">
              <div><span className="font-semibold">Name:</span> {studentName(student)}</div>
              <div><span className="font-semibold">Roll Number:</span> {student.rollNumber || rollNumber}</div>
              <div><span className="font-semibold">Branch:</span> {valueOrDash(student.branch)}</div>
              <div><span className="font-semibold">Division / Batch:</span> {valueOrDash(student.division)} / {valueOrDash(student.batch)}</div>
              <div><span className="font-semibold">Date of Birth:</span> {valueOrDash(student.dob)}</div>
              <div><span className="font-semibold">Mobile:</span> {valueOrDash(student.mobile)}</div>
              <div><span className="font-semibold">Mentor:</span> {mentorName}</div>
              <div><span className="font-semibold">Class Co-ordinator:</span> {coordinatorName}</div>
            </div>
            <div className="flex items-center justify-center border border-gray-400 bg-gray-50">
              {getStudentPhotoUrl(student) ? (
                <img src={getStudentPhotoUrl(student)} alt="Student" className="h-[28mm] w-[28mm] object-cover" />
              ) : (
                <Camera className="h-8 w-8 text-gray-400" />
              )}
            </div>
          </div>

          <section className="mt-4">
            <h2 className="mb-1.5 bg-gray-900 px-2 py-1 text-xs font-bold uppercase text-white">
              Student's Attendance Record
            </h2>
            <table className="w-full table-fixed border-collapse text-[8px] leading-[1.2]">
              <thead>
                <tr className="bg-gray-100">
                  <th rowSpan="2" className="w-[30mm] border border-gray-400 px-1 py-1 text-left">Subject</th>
                  {months.map((month) => (
                    <th key={month} colSpan="2" className="border border-gray-400 px-1 py-1 text-center">{shortMonth(month)}</th>
                  ))}
                  <th rowSpan="2" className="w-[14mm] border border-gray-400 px-1 py-1 text-center">Total</th>
                  <th rowSpan="2" className="w-[12mm] border border-gray-400 px-1 py-1 text-center">%</th>
                  <th rowSpan="2" className="w-[17mm] border border-gray-400 px-1 py-1 text-center">Remark</th>
                </tr>
                <tr className="bg-gray-100">
                  {months.map((month) => (
                    <React.Fragment key={`sub-${month}`}>
                      <th className="border border-gray-400 px-1 py-0.5 text-center">L</th>
                      <th className="border border-gray-400 px-1 py-0.5 text-center">T/P</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {attendanceMatrixRows.map((row) => (
                  <tr key={row.id}>
                    <td className="border border-gray-400 px-1 py-1">
                      <span className="font-semibold">{row.code}</span> {row.name}
                    </td>
                    {row.monthCells.map((cell) => (
                      <React.Fragment key={`${row.id}-${cell.month}`}>
                        <td className="border border-gray-400 px-1 py-1 text-center">
                          {attendanceCell(cell.theoryAttended, cell.theoryConducted)}
                        </td>
                        <td className="border border-gray-400 px-1 py-1 text-center">
                          {attendanceCell(cell.practicalAttended, cell.practicalConducted)}
                        </td>
                      </React.Fragment>
                    ))}
                    <td className="border border-gray-400 px-1 py-1 text-center font-semibold">
                      {attendanceCell(row.totalAttended, row.totalConducted)}
                    </td>
                    <td className="border border-gray-400 px-1 py-1 text-center">
                      {percentage(row.totalAttended, row.totalConducted)}
                    </td>
                    <td className={`border border-gray-400 px-1 py-1 text-center font-semibold ${
                      row.remark === 'Defaulter' ? 'text-red-700' : 'text-gray-800'
                    }`}>
                      {row.remark}
                    </td>
                  </tr>
                ))}
                <tr className="font-bold">
                  <td className="border border-gray-400 px-1 py-1">Semester Overall</td>
                  {semesterAttendance.monthCells.map((cell) => (
                    <React.Fragment key={`overall-${cell.month}`}>
                      <td className="border border-gray-400 px-1 py-1 text-center">
                        {attendanceCell(cell.theoryAttended, cell.theoryConducted)}
                      </td>
                      <td className="border border-gray-400 px-1 py-1 text-center">
                        {attendanceCell(cell.practicalAttended, cell.practicalConducted)}
                      </td>
                    </React.Fragment>
                  ))}
                  <td className="border border-gray-400 px-1 py-1 text-center">
                    {attendanceCell(semesterAttendance.totalAttended, semesterAttendance.totalConducted)}
                  </td>
                  <td className="border border-gray-400 px-1 py-1 text-center">
                    {percentage(semesterAttendance.totalAttended, semesterAttendance.totalConducted)}
                  </td>
                  <td className={`border border-gray-400 px-1 py-1 text-center ${
                    attendanceRemark(semesterAttendance.totalAttended, semesterAttendance.totalConducted) === 'Defaulter'
                      ? 'text-red-700'
                      : 'text-gray-800'
                  }`}>
                    {attendanceRemark(semesterAttendance.totalAttended, semesterAttendance.totalConducted)}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="mt-4">
            <h2 className="mb-1 bg-gray-900 px-2 py-1 text-xs font-bold uppercase text-white">
              Student's Academic Record
            </h2>
            <table className="w-full border-collapse text-[10px]">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-400 px-1 py-1 text-left">Subject</th>
                  <th className="border border-gray-400 px-1 py-1 text-center">IAT-I</th>
                  <th className="border border-gray-400 px-1 py-1 text-left">IAT-I Remark</th>
                  <th className="border border-gray-400 px-1 py-1 text-center">IAT-II</th>
                  <th className="border border-gray-400 px-1 py-1 text-left">IAT-II Remark</th>
                  <th className="border border-gray-400 px-1 py-1 text-center">ESE</th>
                  <th className="border border-gray-400 px-1 py-1 text-left">ESE Remark</th>
                </tr>
              </thead>
              <tbody>
                {academicRows.map((subject) => (
                  <tr key={subject.id}>
                    <td className="border border-gray-400 px-1 py-1">
                      <span className="font-semibold">{subject.code}</span> {subject.name}
                    </td>
                    <td className="border border-gray-400 px-1 py-1 text-center">
                      {formatMark(subject.ia1, subject.iat1Max || subject.iatMax)}
                    </td>
                    <td className="border border-gray-400 px-1 py-1">{valueOrDash(subject.ia1Remark)}</td>
                    <td className="border border-gray-400 px-1 py-1 text-center">
                      {formatMark(subject.ia2, subject.iat2Max || subject.iatMax)}
                    </td>
                    <td className="border border-gray-400 px-1 py-1">{valueOrDash(subject.ia2Remark)}</td>
                    <td className="border border-gray-400 px-1 py-1 text-center">
                      {formatMark(subject.ese, subject.eseMax)}
                    </td>
                    <td className="border border-gray-400 px-1 py-1">{valueOrDash(subject.eseRemark)}</td>
                  </tr>
                ))}
                <tr className="font-bold">
                  <td className="border border-gray-400 px-1 py-1">Total</td>
                  <td className="border border-gray-400 px-1 py-1 text-center">
                    {formatTotal(hasIa1, totalIa1, totalIa1Max)}
                  </td>
                  <td className="border border-gray-400 px-1 py-1">-</td>
                  <td className="border border-gray-400 px-1 py-1 text-center">
                    {formatTotal(hasIa2, totalIa2, totalIa2Max)}
                  </td>
                  <td className="border border-gray-400 px-1 py-1">-</td>
                  <td className="border border-gray-400 px-1 py-1 text-center">
                    {formatTotal(hasEse, totalEse, totalEseMax)}
                  </td>
                  <td className="border border-gray-400 px-1 py-1">-</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="mt-3">
            <h2 className="mb-1.5 bg-gray-900 px-2 py-1 text-xs font-bold uppercase text-white">
              Parent's Meet / Visit
            </h2>
            <table className="w-full border-collapse text-[9.5px]">
              <tbody>
                <tr>
                  <td className="w-[18mm] border border-gray-400 px-1 py-1 font-semibold">Date</td>
                  <td className="border border-gray-400 px-1 py-1">{formatDate(parentMeet?.meetingDate)}</td>
                  <td className="w-[18mm] border border-gray-400 px-1 py-1 font-semibold">Mode</td>
                  <td className="border border-gray-400 px-1 py-1">{valueOrDash(parentMeet?.mode)}</td>
                  <td className="w-[18mm] border border-gray-400 px-1 py-1 font-semibold">Parent</td>
                  <td className="border border-gray-400 px-1 py-1">
                    {valueOrDash(parentMeet?.parentName)} {parentMeet?.relation ? `(${parentMeet.relation})` : ''}
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-1 py-1 font-semibold">Discussion</td>
                  <td className="h-[10mm] border border-gray-400 px-1.5 py-1 align-top" colSpan="5">
                    {valueOrDash(parentMeet?.pointsDiscussed)}
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-1 py-1 font-semibold">Action</td>
                  <td className="h-[10mm] border border-gray-400 px-1.5 py-1 align-top" colSpan="3">
                    {valueOrDash(parentMeet?.actionPlan)}
                  </td>
                  <td className="w-[24mm] border border-gray-400 px-1 py-1 text-center font-semibold">
                    Parent's Sign
                  </td>
                  <td className="w-[35mm] border border-gray-400 px-1 py-1" />
                </tr>
              </tbody>
            </table>
          </section>

          <section className="mt-3">
            <h2 className="mb-1.5 bg-gray-900 px-2 py-1 text-xs font-bold uppercase text-white">
              Signatures
            </h2>
            <div className="grid grid-cols-4 gap-2 text-center text-[9px]">
              <div className="border border-gray-400 px-2 pb-2 pt-5">
                <div className="border-t border-gray-500 pt-1 font-semibold">Mentor</div>
                <div className="mt-0.5 text-[8px]">{mentorName}</div>
              </div>
              <div className="border border-gray-400 px-2 pb-2 pt-5">
                <div className="border-t border-gray-500 pt-1 font-semibold">Class Co-ordinator</div>
                <div className="mt-0.5 text-[8px]">{coordinatorName}</div>
              </div>
              <div className="border border-gray-400 px-2 pb-2 pt-5">
                <div className="border-t border-gray-500 pt-1 font-semibold">HOD</div>
              </div>
              <div className="border border-gray-400 px-2 pb-2 pt-5">
                <div className="border-t border-gray-500 pt-1 font-semibold">Principal</div>
              </div>
            </div>
          </section>

          <div className="mt-2 border-t border-gray-300 pt-1 text-center text-[8.5px] text-gray-600">
            Generated on {new Date().toLocaleDateString('en-IN')}
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { margin: 0; padding: 0; }
          .print\\:hidden { display: none !important; }
          .print\\:bg-white { background: white !important; }
          .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:px-0 { padding-left: 0 !important; padding-right: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          @page { size: A4; margin: 0; }
        }
      `}</style>
    </div>
  );
};

export default EndSemesterReport;
